import React from "react"
import {
  type AgingSummaryChartConfig,
  Interval,
  type LayoutComponentType,
  useReadAgingSummaryChartLiveDataQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

export function useAgingSummaryChartLiveData(
  baseConfig: AgingSummaryChartConfig | undefined | null,
  type: LayoutComponentType,
  { noLaterThanToday = true, skip }: { noLaterThanToday?: boolean; skip?: boolean } = {}
) {
  const origin = useIntervalOrigin()
  const config = React.useMemo(() => {
    if (!baseConfig) {
      return undefined
    }
    if (noLaterThanToday) {
      const end = dateTimeHelper.endOfIntervalOrigin(origin)
      // Do not query for future dates
      if (end.isAfter(dayjs().startOf("day").utc())) {
        return {
          ...baseConfig,
          origin: dateTimeHelper.intervalOriginFromDayjs(
            dayjs().startOf("day").utc(),
            Interval.Day
          ),
        }
      }
    }
    return { ...baseConfig, origin }
  }, [baseConfig, origin, noLaterThanToday])
  const viewId = useViewVersion()

  const { data, loading } = useReadAgingSummaryChartLiveDataQuery({
    variables: {
      viewId,
      config: {
        type,
        agingSummaryChart: config,
      },
    },
    skip: skip || !viewId?.viewVersion,
  })

  return React.useMemo(() => {
    const liveData = data?.liveComponentData

    // Resolves the component data union types to the portions we care about
    if (liveData?.__typename === "Chart") {
      return { category: undefined, dimensionalSummary: liveData.summary, loading }
    }

    return { category: undefined, dimensionalSummary: undefined, loading }
  }, [data?.liveComponentData, loading])
}
